import { useContext } from 'react';
export function getRecommendedModule(userState) {
    // pretty much an exact copy of global.getRecommendedModule() from talentshow-functions
    // except for functionality mapping the original rec module value
    // to a string more suitable for displaying on screen
    const eligibleModules = userState.parabolaModel.eligibleModules || [];
    const completedModules = userState.parabolaModel.completedModules || [];
    // Remove every string that appears in both arrays and the current module,
    // returning an array with only modules the user hasn't finished.
    const incompleteModules = typeof eligibleModules === 'undefined'
        ? eligibleModules
        : eligibleModules.filter((val) => !completedModules.includes(val));
    // Return the 'topmost' available module left to complete in the menu.
    // If everything is done, return 'none'!
    return incompleteModules[0] || null;
}
export function getModuleNameDisplayText(modulesState, moduleId // Constant value matching the current module 'eap', 'tax_savings', 'enrollment', etc. Null if no recommended module.
) {
    if (typeof moduleId === 'string') {
        return (modulesState.filter((module) => module.id === moduleId)[0]?.name || null);
    }
    return null;
}
export function formatAsCurrency(value) {
    // Returns a string representation of an amount of currency in USD.
    // pretty much an exact copy of math.formatAsCurrency() from talentshow-functions
    value = Number(value);
    const cents = value.toFixed(2).split('.')[1];
    const minDigits = cents === '00' ? 0 : 2;
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: minDigits,
    });
    return formatter.format(value);
}
export function formatNumeral(num) {
    const stringifiedNum = num.toString();
    const valueMap = {
        '1': 'one',
        '2': 'two',
        '3': 'three',
        '4': 'four',
        '5': 'five',
        '6': 'six',
        '7': 'seven',
        '8': 'eight',
        '9': 'nine',
        '10': 'ten',
    };
    return valueMap[stringifiedNum] || stringifiedNum;
}
export function getFirst(list) {
    const firstItem = list[0];
    if (!firstItem) {
        throw `Expected list to contain at least one item`;
    }
    return firstItem;
}
/**  the empty paragraph is what kentico sends when there is no content inside the rich html field
in the cms. basically, it's the empty value. */
export function richHTMLFieldHasValue(fieldValue) {
    return fieldValue !== '<p><br></p>';
}
export function getTextNotes(linkedNote, planData) {
    const note = linkedNote.linkedItems[0];
    if (!note) {
        return {
            shortNote: undefined,
            fullText: undefined,
        };
    }
    const shortNoteKey = note.elements.short_note_key.value;
    const fullTextKey = note.elements.full_text_key.value;
    const shortNote = planData[shortNoteKey];
    const fullText = fullTextKey !== ''
        ? planData[fullTextKey]
        : undefined;
    return {
        shortNote,
        fullText,
    };
}
/**
 * Create a React hook that enforces that the given hook has been used within a Provider.
 *
 * This approach has been taken from here:
 * https://react-typescript-cheatsheet.netlify.app/docs/basic/getting-started/context/#without-default-context-value
 */
export function buildUseContextHook(context, hookName, providerName) {
    return function () {
        const contextValue = useContext(context);
        if (!contextValue) {
            throw new Error(`${hookName} has to be used within Provider <${providerName}>`);
        }
        return contextValue;
    };
}
export function getNumEligibleChildren(numKids, dependents, availableToChildren, maxAgeChildren) {
    if (numKids && availableToChildren) {
        // dependents array is created by the Profile Builder on the Comm API path
        if (dependents?.length > 0) {
            const childArray = dependents.filter((dependent) => dependent.relationship === 'child');
            // return number of children that are under the age limit
            // or the total number of children (if num under the age limit can't be determined)
            return childArray.filter((child) => {
                if (maxAgeChildren && child.age) {
                    return child.age < maxAgeChildren;
                }
                return true;
            }).length;
        }
        // if dependents array doesn't exist for some reason (BC Calc)
        // return the total number of children
        return numKids;
    }
    return 0;
}
/**
 * Determines if the user's spouse or eligible partner is eligible for this benefit
 *
 * @param spouseType if the user is single ('None'), has a spouse ('Spouse'), or a domestic partner ('SSDP')
 * @param planHasSignificantOtherCoverage if the plan allows the user to cover their significant other (spouse/partner)
 * @param benefitIsAvailableToDomesticPartners if the benefit is available to domestic partners. Configured in the *Product* settings in Builder and merged into the plan data by horus-shared.
 * @returns
 */
export function checkIfSignificantOtherIsEligible(spouseType, planHasSignificantOtherCoverage, benefitIsAvailableToDomesticPartners) {
    if (!planHasSignificantOtherCoverage) {
        return false;
    }
    if (spouseType === 'Spouse') {
        return true;
    }
    // Even if the plan's configuration has spouse/partner coverage toggled on
    // The benefit overall may not be available to domestic partners (configured in the product settings, rather than the plan)
    // Which is why we have this separate check
    if (spouseType === 'SSDP' && benefitIsAvailableToDomesticPartners) {
        return true;
    }
    return false;
}
export function getSelectionStatusVerb(selectionStatus) {
    if (selectionStatus === 'Selected') {
        return 'would';
    }
    if (selectionStatus === 'Waived') {
        return `wouldn't`;
    }
    return null;
}
export function generateLinkHtml(url, text = url) {
    return url
        ? `<a href="${url}" target="_blank" data-new-window="true">${text}</a>`
        : null;
}
export function generateListHtml(values) {
    if (!values || values.length === 0) {
        return null;
    }
    return `<ul>${values
        .map((value) => (value ? `<li>${value}</li>` : ''))
        .join('')}</ul>`;
}
export function getChildVsChildren(childrenNum) {
    if (childrenNum && childrenNum > 0) {
        return childrenNum === 1 ? 'child' : 'children';
    }
    return null;
}
export function getEligibleMembersDisplayText(numKids, eligibleKidsNum, spouseTerm, hasEligibleSignificantOther) {
    let childCoverageString = '';
    // Has eligible kid(s)
    if (numKids && eligibleKidsNum) {
        // if plan has an age limit for child covg & we know kids' ages
        // then some kids might not be eligible
        childCoverageString =
            eligibleKidsNum === numKids
                ? `${getChildVsChildren(eligibleKidsNum)}`
                : `${formatNumeral(eligibleKidsNum)} of your children`;
        // if the user also has an eligible spouse/partner
        if (hasEligibleSignificantOther) {
            if (eligibleKidsNum === numKids) {
                return 'family';
            }
            return `${spouseTerm} and ${childCoverageString}`;
        }
        return childCoverageString;
    }
    // No eligible kids, has eligible spouse/partner
    if (hasEligibleSignificantOther) {
        return spouseTerm;
    }
    // No eligible family
    return null;
}
export function getCoveredMembersDisplayText(numKids, coverKidsNum, spouseTerm, coverSpouse) {
    let childCoverageString = '';
    if (coverKidsNum) {
        childCoverageString =
            coverKidsNum === numKids
                ? `and your ${getChildVsChildren(coverKidsNum)}`
                : `and ${formatNumeral(coverKidsNum)} of your children`;
    }
    // User is covering their spouse/partner and at least 1 of their children;
    if (coverKidsNum && coverSpouse) {
        return numKids === coverKidsNum && numKids > 1
            ? 'yourself and your family'
            : `yourself, your ${spouseTerm}, ${childCoverageString}`;
    }
    // User is covering their spouse/partner but not any children:
    if (coverSpouse && !coverKidsNum) {
        return `yourself and your ${spouseTerm}`;
    }
    // User is covering 1 or more children, but not a spouse/partner:
    if (!coverSpouse && coverKidsNum) {
        return `yourself ${childCoverageString}`;
    }
    // If the user is not covering any dependents:
    return 'yourself';
}
//# sourceMappingURL=utilities.js.map